import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";

const CORRECTION_SESSION_PREFIX = "sokogrid:owner-corrections:";
const CORRECTION_SESSION_TTL_MS = 4 * 60 * 60 * 1000;

type StoredCorrectionSession = {
  ownerId: string;
  businessId: string;
  expiresAt: number;
};

function correctionSessionKey(ownerId: string) {
  return `${CORRECTION_SESSION_PREFIX}${ownerId}`;
}

async function setSessionValue(key: string, value: string) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.sessionStorage.setItem(key, value);
    return;
  }
  await AsyncStorage.setItem(key, value);
}

async function getSessionValue(key: string) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return window.sessionStorage.getItem(key);
  }
  return AsyncStorage.getItem(key);
}

async function removeSessionValue(key: string) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.sessionStorage.removeItem(key);
    return;
  }
  await AsyncStorage.removeItem(key);
}

export async function startOwnerCorrectionSession(
  ownerId: string,
  businessId: string
) {
  const session: StoredCorrectionSession = {
    ownerId,
    businessId,
    expiresAt: Date.now() + CORRECTION_SESSION_TTL_MS,
  };

  await setSessionValue(
    correctionSessionKey(ownerId),
    JSON.stringify(session)
  );
}

export async function hasOwnerCorrectionSession(
  ownerId: string,
  businessId: string
) {
  const key = correctionSessionKey(ownerId);
  const raw = await getSessionValue(key);
  if (!raw) return false;

  try {
    const session = JSON.parse(raw) as Partial<StoredCorrectionSession>;
    const valid =
      session.ownerId === ownerId &&
      session.businessId === businessId &&
      typeof session.expiresAt === "number" &&
      session.expiresAt > Date.now();

    if (!valid) {
      await removeSessionValue(key);
    }

    return valid;
  } catch {
    await removeSessionValue(key);
    return false;
  }
}

export async function clearOwnerCorrectionSession(ownerId: string) {
  await removeSessionValue(correctionSessionKey(ownerId));
}
